import { ConfigType } from "./abstract/Form";
import { EthnicityType, ToolType } from "./types";
import { DefaultTheme, useTheme } from "styled-components";
import { colors, ReportColorStyle } from "./theme";
import { formatDate as fnsFormatDate } from "date-fns";
import { SupportedLanguage, languages } from "./constants";

export const kgToPounds = (kg: number): number => {
  const pounds = kg * 2.20462;
  return Math.round(pounds * 100) / 100;
};

export const toYYYYMM = (date: Date): string => {
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const year = date.getFullYear();
  return `${year}-${month}`;
}

export const cmToFeetInches = (cm: number): [number, number] => {
  const totalInches = cm / 2.54;
  const feet = Math.floor(totalInches / 12);
  const inches = Math.round(totalInches % 12);
  return [feet, inches];
};

export const poundsToKg = (pounds: number): number => {
  const kg = pounds / 2.20462;
  return Math.round(kg * 100) / 100;
};

export const feetInchesToCm = (feet: number, inches: number): number => {
  const totalInches = feet * 12 + inches;
  const cm = totalInches * 2.54;
  return Math.round(cm * 100) / 100;
};

export const getMaskedEmail = (email: string): string => {
  if (!email) return "";
  const [localPart, domain] = email.split("@");
  return `${localPart.slice(0, 3)}*****@${domain}`;
};

export const formatDate = (date: Date): string => {
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are 0-indexed
  const year = String(date.getFullYear()).slice(-2); // Get last 2 digits of the year
  return `${day}/${month}/${year}`;
};

export const toKebabCase = (str: string): string => {
  return str.replace(" ", "-");
};

export const convertImageToBase64 = (url: string): Promise<string> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "Anonymous";
    img.src = url;
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext("2d");
      if (ctx) {
        ctx.drawImage(img, 0, 0);
        const dataURL = canvas.toDataURL("image/png");
        resolve(dataURL);
      } else {
        reject(new Error("Could not create canvas context"));
      }
    };
    img.onerror = (error) => reject(error);
  });
};

export const stripBase64Prefix = (base64: string): string => {
  return base64.replace(/^data:image\/[a-z]+;base64,/, "");
};

export const unflattenObject = (
  obj: Record<string, any>
): Record<string, any> => {
  const result: Record<string, any> = {};
  for (const [key, value] of Object.entries(obj)) {
    const keys = key.split(".");
    let current = result;
    while (keys.length > 1) {
      const part = keys.shift() as string;
      current = current[part] = current[part] || {};
    }
    current[keys[0]] = value;
  }
  return result;
};

export const findConfigFieldByName = (
  config: ConfigType,
  fieldName: string
) => {
  for (const section of config.right) {
    if ("rows" in section) {
      for (const row of section.rows ?? []) {
        for (const field of row.fields) {
          if (field.name === fieldName) {
            return field;
          }
        }
      }
    }
    if ("subsections" in section) {
      for (const subsection of section.subsections ?? []) {
        for (const row of subsection.rows ?? []) {
          for (const field of row.fields) {
            if (field.name === fieldName) {
              return field;
            }
          }
        }
      }
    }
  }
  return null;
};

export const getBucketItemPath = (
  userId: string,
  tool: ToolType,
  cycle: string,
  id: number,
  extension: string
): string => {
  return `private/${userId}/${tool}/${cycle}/${id}.${extension}`;
};

export const getAge = (birthDate: Date) => {
  const diff = new Date().getTime() - birthDate.getTime();
  const ageDate = new Date(diff);
  return Math.abs(ageDate.getUTCFullYear() - 1970);
};

export const getInitials = (name: string): string => {
  if (name.indexOf(" ") === -1) {
    return name.slice(0, 2);
  }
  return name
    .split(" ")
    .map((n) => n[0])
    .slice(0, 2)
    .join("");
};

export function enumKeyByValue(enumObj: any, enumVal: any) {
  let keys = Object.keys(enumObj).filter(x => enumObj[x] == enumVal);
  return keys.length > 0 ? keys[0] : null;
}

export const enumToOptions = (enumObj: any) => {
  return Object.keys(enumObj)
    .filter((key) => isNaN(Number(key)))
    .map((key) => ({
      label: key,
      value: `${enumObj[key as keyof typeof enumObj]}`,
    }));
};

export const literalDate = (date: Date): string => {
  return fnsFormatDate(date, "EEE, d MMMM yyyy");
};

export const getCurrentColorScheme = (
  theme: DefaultTheme,
  colorStyle: string
): ReportColorStyle => {
  return theme.palette.reportColorStyles[colorStyle];
};

const reExt = /[^.](?:\.([^.]+))?$/;
export const getFileExtension = (path: string) => {
  var res = reExt.exec(path);
  return res === null ? "" : res[1];
};

export const VIDEO_EXTENSIONS = ["avi", "mp4"];

export const scoreColor = (score: number) => {
  if (score < 20) {
    return colors.red[100];
  } else if (score < 60) {
    return colors.orange[100];
  } else if (score < 80) {
    return colors.yellow[100];
  } else {
    return colors.green[100];
  }
};

export const clampPercentage = (num: number): number => {
  return Math.min(100, Math.max(0, num));
};

export const scrollToElement = (element: HTMLElement) => {
  console.info("scrolling to error", element);
  const yOffset = -120;
  const y = element.getBoundingClientRect().top + window.scrollY + yOffset;
  window.scrollTo({ top: y, behavior: "smooth" });
};

export const isRtlLanguage = (lang: SupportedLanguage): boolean => {
  return languages[lang].rtl ?? false;
}